import { useEffect, useMemo, useRef, useState } from "react";
import { Lightbulb, RotateCcw, Scale, Undo2 } from "lucide-react";
import { Button, useToast } from "../../ui/kit";
import type { BoardProps } from "../types";
import { specFor } from "../catalog";
import { kindOf, phaseOf, replay, stories, suggestWeighing, verdict, type CoinsSpec, type Outcome } from "./model";

interface CoinState {
  ws: { l: number[]; r: number[] }[];
  dl: number[];
  dr: number[];
  notes: boolean;
  peeked: boolean;
}

const fresh = (): CoinState => ({ ws: [], dl: [], dr: [], notes: true, peeked: false });

function ints(v: unknown, n: number): number[] {
  return Array.isArray(v) ? [...new Set(v.filter((x): x is number => Number.isInteger(x) && x >= 0 && x < n))] : [];
}

function sanitize(saved: unknown, spec: CoinsSpec): CoinState {
  const s = saved as Partial<CoinState> | null;
  if (!s || typeof s !== "object") return fresh();
  const ws: CoinState["ws"] = [];
  for (const w of Array.isArray(s.ws) ? s.ws.slice(0, spec.weighings) : []) {
    const l = ints(w?.l, spec.n);
    const r = ints(w?.r, spec.n).filter((i) => !l.includes(i));
    if (l.length && l.length === r.length) ws.push({ l, r });
  }
  const dl = ints(s.dl, spec.n);
  const dr = ints(s.dr, spec.n).filter((i) => !dl.includes(i));
  return { ws, dl, dr, notes: s.notes !== false, peeked: s.peeked === true };
}

const TILT = 9; // degrees

const outcomeText = (o: Outcome): string => (o === "=" ? "The pans balanced." : o === "L" ? "The left pan is heavier." : "The right pan is heavier.");
const shortOutcome = (o: Outcome): string => (o === "=" ? "balanced" : o === "L" ? "left heavier" : "right heavier");

function PanCoins({ coins, onTap, fake }: { coins: number[]; onTap?: (i: number) => void; fake?: number | null }) {
  const perRow = 7;
  const rows: number[][] = [];
  for (let i = 0; i < coins.length; i += perRow) rows.push(coins.slice(i, i + perRow));
  return (
    <g>
      {rows.map((row, j) =>
        row.map((c, k) => {
          const x = (k - (row.length - 1) / 2) * 24.5;
          const y = 57 - j * 24;
          return (
            <g key={c} transform={`translate(${x} ${y})`} className={"pan-coin" + (fake === c ? " fake" : "")} onClick={onTap ? (e) => { e.stopPropagation(); onTap(c); } : undefined} style={{ cursor: onTap ? "pointer" : "default" }}>
              <circle r={11} className="pan-coin-face" />
              <text textAnchor="middle" dominantBaseline="central" className="pan-coin-num">{c + 1}</text>
            </g>
          );
        })
      )}
    </g>
  );
}

export default function CoinsBoard({ id, saved, hintLevel, showAnswer, restartToken, onState, onSolved, onPeek }: BoardProps) {
  const spec = (specFor(id) as { spec: CoinsSpec }).spec;
  const toast = useToast();
  const [state, setState] = useState<CoinState>(() => sanitize(saved, spec));
  const [active, setActive] = useState<"l" | "r">("l");
  /** the arrangement of the weighing just made stays on the scale until the player touches a coin */
  const [shown, setShown] = useState<{ l: number[]; r: number[]; out: Outcome } | null>(null);
  const solvedRef = useRef<boolean | null>(null);
  if (solvedRef.current === null) solvedRef.current = phaseOf(spec, replay(spec, state.ws).poss, state.ws.length) === "solved";
  const stateRef = useRef(state);
  stateRef.current = state;

  const { poss, outcomes } = useMemo(() => replay(spec, state.ws), [spec, state.ws]);
  const used = state.ws.length;
  const phase = phaseOf(spec, poss, used);
  const kind = kindOf(poss);
  const fake = phase === "solved" ? verdict(poss) : null;
  const cap = Math.floor(spec.n / 2);

  const update = (next: CoinState) => {
    setState(next);
    onState(next);
  };

  // restart
  const lastToken = useRef(restartToken);
  useEffect(() => {
    if (lastToken.current === restartToken) return;
    lastToken.current = restartToken;
    solvedRef.current = false;
    setShown(null);
    update({ ...fresh(), notes: stateRef.current.notes });
  }, [restartToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === "solved" && !solvedRef.current) {
      solvedRef.current = true;
      onSolved(`${used} weighing${used === 1 ? "" : "s"}`, state.peeked);
    }
    if (phase !== "solved") solvedRef.current = false;
  }, [phase, used, state.peeked, onSolved]);

  // "show me": drop a good weighing on the scale once when the answer is asked for
  const shownAnswer = useRef(false);
  useEffect(() => {
    if (showAnswer && !shownAnswer.current) {
      shownAnswer.current = true;
      suggest();
    }
    if (!showAnswer) shownAnswer.current = false;
  }, [showAnswer]); // eslint-disable-line react-hooks/exhaustive-deps

  const tapCoin = (i: number) => {
    if (phase === "solved") return;
    setShown(null);
    const s = stateRef.current;
    const base = shown ? { ...s, dl: [], dr: [] } : s;
    if (base.dl.includes(i)) return update({ ...base, dl: base.dl.filter((c) => c !== i) });
    if (base.dr.includes(i)) return update({ ...base, dr: base.dr.filter((c) => c !== i) });
    const side = active === "l" ? base.dl : base.dr;
    if (side.length >= cap) {
      toast(`A pan can hold at most ${cap} coins here.`);
      return update(base);
    }
    update(active === "l" ? { ...base, dl: [...base.dl, i].sort((a, b) => a - b) } : { ...base, dr: [...base.dr, i].sort((a, b) => a - b) });
  };

  const weigh = () => {
    const s = stateRef.current;
    if (!s.dl.length || s.dl.length !== s.dr.length || used >= spec.weighings) return;
    const ws = [...s.ws, { l: s.dl, r: s.dr }];
    const out = replay(spec, ws).outcomes[ws.length - 1];
    setShown({ l: s.dl, r: s.dr, out });
    update({ ...s, ws, dl: [], dr: [] });
  };

  const undoWeighing = () => {
    const s = stateRef.current;
    if (!s.ws.length) return;
    setShown(null);
    update({ ...s, ws: s.ws.slice(0, -1), dl: [], dr: [] });
  };

  const startOver = () => {
    setShown(null);
    update({ ...fresh(), notes: stateRef.current.notes });
  };

  function suggest() {
    const s = stateRef.current;
    const w = suggestWeighing(spec, replay(spec, s.ws).poss, s.ws.length);
    if (!w) {
      toast("From here no weighing can promise an answer. Undo the last weighing.");
      return;
    }
    setShown(null);
    update({ ...s, dl: w.l, dr: w.r, peeked: true });
    onPeek?.();
  }

  const onPans = shown ? { l: shown.l, r: shown.r } : { l: state.dl, r: state.dr };
  const tilt = shown ? (shown.out === "L" ? -TILT : shown.out === "R" ? TILT : 0) : 0;
  const equal = state.dl.length === state.dr.length && state.dl.length > 0;
  const canWeigh = equal && used < spec.weighings && phase !== "solved" && !shown;
  const draftOnPans = new Set([...onPans.l, ...onPans.r]);
  const showCount = hintLevel >= 2;
  const suspects = poss.filter(Boolean).length;
  const status =
    phase === "solved"
      ? fake
        ? `Coin ${fake.coin + 1} is the fake${fake.heavier === null ? "" : fake.heavier ? ", and it is heavier" : ", and it is lighter"}. You found it with ${used} weighing${used === 1 ? "" : "s"}!`
        : "Solved!"
      : phase === "stuck"
        ? `You're out of weighings and ${suspects} coins are still suspects. Undo a weighing or start over and try a different plan.`
        : phase === "doomed"
          ? "With the weighings you have left, you can no longer be certain. Undo the last weighing, or start over."
          : shown
            ? outcomeText(shown.out)
            : `Weighings used: ${used} of ${spec.weighings}.`;

  const attach = { l: { x: 90, y: 60 }, r: { x: 510, y: 60 } };

  return (
    <div className="board coins-board">
      <div className="wood-card">
        <svg viewBox="0 0 600 270" className="scale-svg" role="img" aria-label="A balance scale">
          <defs>
            <linearGradient id="brass" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e9c976" />
              <stop offset="55%" stopColor="#c99a3c" />
              <stop offset="100%" stopColor="#9d7423" />
            </linearGradient>
          </defs>
          {/* stand */}
          <rect x="294" y="60" width="12" height="180" rx="6" fill="url(#brass)" />
          <rect x="222" y="234" width="156" height="16" rx="8" fill="url(#brass)" />
          {/* beam and pans */}
          <g className="beam" style={{ transform: `rotate(${tilt}deg)`, transformOrigin: "300px 60px" }}>
            <rect x="80" y="54" width="440" height="12" rx="6" fill="url(#brass)" />
            {(["l", "r"] as const).map((side) => (
              <g key={side} transform={`translate(${attach[side].x} ${attach[side].y})`}>
                <g className="pan-hang" style={{ transform: `rotate(${-tilt}deg)`, transformOrigin: "0px 0px" }}>
                  <path d="M0 0 L-78 74 M0 0 L78 74" stroke="#9d7423" strokeWidth="2.2" fill="none" />
                  <PanCoins coins={onPans[side]} onTap={shown ? undefined : tapCoin} fake={null} />
                  <path d="M-88 74 Q0 118 88 74 Z" fill="url(#brass)" className={"pan-bowl" + (active === side && !shown && phase !== "solved" ? " active" : "")} />
                  <rect x="-100" y="20" width="200" height="110" fill="transparent" onClick={() => { if (!shown) setActive(side); }} style={{ cursor: "pointer" }} />
                  <text x="0" y="146" textAnchor="middle" className="pan-label">{side === "l" ? "left pan" : "right pan"}{onPans[side].length ? ` · ${onPans[side].length}` : ""}</text>
                </g>
              </g>
            ))}
            <circle cx="300" cy="60" r="11" fill="url(#brass)" stroke="#7a5a17" strokeWidth="1.5" />
          </g>
        </svg>
      </div>

      <p className={"board-status" + (phase === "solved" ? " good" : phase === "doomed" || phase === "stuck" ? " warn" : "")} aria-live="polite">{status}</p>

      <div className="pan-picker" role="group" aria-label="Which pan to fill">
        <span className="muted small">Tap coins to put them on the</span>
        <div className="seg">
          <button type="button" className={active === "l" ? "on" : ""} onClick={() => setActive("l")}>left pan</button>
          <button type="button" className={active === "r" ? "on" : ""} onClick={() => setActive("r")}>right pan</button>
        </div>
      </div>

      <div className="coin-bench">
        {Array.from({ length: spec.n }, (_, i) => {
          const p = poss[i];
          const known = state.notes && used > 0 && p === 0;
          const mark = state.notes && spec.mode !== "heavy" && p !== 0 && used > 0 ? (p === 1 ? "▲" : p === 2 ? "▼" : "◆") : "";
          const gone = draftOnPans.has(i);
          return (
            <button
              key={i} type="button" onClick={() => tapCoin(i)} disabled={phase === "solved" && !(fake && fake.coin === i)}
              className={"coin" + (known ? " genuine" : "") + (gone ? " gone" : "") + (fake && fake.coin === i ? " fake" : "")}
              aria-label={`Coin ${i + 1}${known ? ", known to be genuine" : ""}${gone ? ", on a pan" : ""}`}
            >
              <span className="coin-n">{i + 1}</span>
              {mark && <span className="coin-mark" aria-hidden="true">{mark}</span>}
              {known && <span className="coin-tick" aria-hidden="true">✓</span>}
            </button>
          );
        })}
      </div>

      <div className="board-tools">
        <Button kind="pond" onClick={weigh} disabled={!canWeigh}><Scale size={17} /> Weigh</Button>
        <Button size="small" onClick={() => { setShown(null); update({ ...state, dl: [], dr: [] }); }} disabled={!state.dl.length && !state.dr.length && !shown}>Clear pans</Button>
        <Button size="small" onClick={undoWeighing} disabled={!used}><Undo2 size={17} /> Undo weighing</Button>
        <Button size="small" onClick={startOver} disabled={!used && !state.dl.length && !state.dr.length}><RotateCcw size={16} /> Start over</Button>
      </div>
      {!equal && (state.dl.length > 0 || state.dr.length > 0) && phase !== "solved" && !shown && (
        <p className="muted small center">Put the same number of coins on each pan ({state.dl.length} vs {state.dr.length}).</p>
      )}

      <div className="coin-extras">
        <label className="check-row">
          <input type="checkbox" checked={state.notes} onChange={(e) => update({ ...state, notes: e.target.checked })} />
          <span>Show what I have worked out (✓ genuine{spec.mode !== "heavy" ? ", ▲ could be heavy, ▼ could be light" : ""})</span>
        </label>
        {showCount && phase !== "solved" && (
          <p className="tally"><Lightbulb size={16} /> {spec.mode === "heavy" ? "Suspects left" : "Possibilities left"}: <strong>{spec.mode === "heavy" ? suspects : stories(kind)}</strong>{" "}
            <span className="muted small">· one weighing can shrink this by at most a factor of 3</span></p>
        )}
        {(hintLevel >= 3 || showAnswer) && phase !== "solved" && (
          <Button size="small" onClick={suggest}><Lightbulb size={16} /> Suggest a weighing</Button>
        )}
      </div>

      {state.ws.length > 0 && (
        <ol className="weigh-log" aria-label="Your weighings">
          {state.ws.map((w, i) => (
            <li key={i}>
              <span className="log-n">{i + 1}</span>
              <span className="log-side">{w.l.map((c) => c + 1).join(" ")}</span>
              <span className="muted">vs</span>
              <span className="log-side">{w.r.map((c) => c + 1).join(" ")}</span>
              <span className={"log-out " + outcomes[i]}>{shortOutcome(outcomes[i])}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
